import api from './api';

class MasterDataService {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
    this.isLoading = false;
    this.loadingPromises = new Map();
  }

  // Get values for a specific category
  async getValuesByCategory(categoryName) {
    const cacheKey = `category_${categoryName}`;
    
    // Check if we have a valid cached version
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Check if we're already loading this category
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Create a new loading promise
    const loadingPromise = this.fetchValuesByCategory(categoryName, cacheKey);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const result = await loadingPromise;
      return result;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  // Get all master data values (for comprehensive caching)
  async getAllValues() {
    const cacheKey = 'all_values';
    
    // Check if we have a valid cached version
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Check if we're already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Create a new loading promise
    const loadingPromise = this.fetchAllValues(cacheKey);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const result = await loadingPromise;
      return result;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  // Get all categories
  async getCategories() {
    const cacheKey = 'categories';
    
    // Check if we have a valid cached version
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Check if we're already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Create a new loading promise
    const loadingPromise = this.fetchCategories(cacheKey);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const result = await loadingPromise;
      return result;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  // Search values
  async searchValues(query, category = null, limit = 20) {
    try {
      const params = new URLSearchParams({
        q: query,
        limit: limit.toString()
      });
      
      if (category) {
        params.append('category', category);
      }

      const response = await api.get(`/api/master-data/search?${params}`);
      return response.data.values || [];
    } catch (error) {
      console.error('Error searching master data values:', error);
      throw error;
    }
  }

  // Get a specific value by ID
  async getValueById(id) {
    try {
      const response = await api.get(`/api/master-data/values/id/${id}`);
      return response.data.value;
    } catch (error) {
      console.error('Error fetching master data value by ID:', error);
      throw error;
    }
  }

  // Refresh cache for a specific category
  async refreshCategory(categoryName) {
    const cacheKey = `category_${categoryName}`;
    this.cache.delete(cacheKey);
    this.cacheExpiry.delete(cacheKey);
    return this.getValuesByCategory(categoryName);
  }

  // Refresh all cache
  async refreshAll() {
    this.cache.clear();
    this.cacheExpiry.clear();
    return this.getAllValues();
  }

  // Check if cache is valid
  isCacheValid(cacheKey) {
    const expiry = this.cacheExpiry.get(cacheKey);
    return expiry && Date.now() < expiry;
  }

  // Set cache with expiry
  setCache(cacheKey, data) {
    this.cache.set(cacheKey, data);
    this.cacheExpiry.set(cacheKey, Date.now() + this.cacheDuration);
  }

  // Fetch values for a specific category
  async fetchValuesByCategory(categoryName, cacheKey) {
    try {
      const response = await api.get(`/api/master-data/values/${categoryName}`);
      const data = response.data.values || [];
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Error fetching values for category ${categoryName}:`, error);
      throw error;
    }
  }

  // Fetch all values
  async fetchAllValues(cacheKey) {
    try {
      const response = await api.get('/api/master-data/values');
      const data = response.data.values || {};
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching all master data values:', error);
      throw error;
    }
  }

  // Fetch categories
  async fetchCategories(cacheKey) {
    try {
      const response = await api.get('/api/master-data/categories');
      const data = response.data.categories || [];
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching master data categories:', error);
      throw error;
    }
  }

  // Get values as simple array (for backward compatibility)
  async getValuesArray(categoryName) {
    const values = await this.getValuesByCategory(categoryName);
    return values.map(item => item.value);
  }

  // Get values as options array for select components
  async getOptionsArray(categoryName) {
    const values = await this.getValuesByCategory(categoryName);
    return values.map(item => ({
      value: item.value,
      label: item.display_name || item.value,
      description: item.description,
      metadata: item.metadata
    }));
  }

  // Health check
  async healthCheck() {
    try {
      const response = await api.get('/api/master-data/health');
      return response.data;
    } catch (error) {
      console.error('Master data service health check failed:', error);
      return { status: 'error', message: 'Service unavailable' };
    }
  }

  // Clear all cache
  clearCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  // Get cache statistics
  getCacheStats() {
    return {
      cacheSize: this.cache.size,
      cacheKeys: Array.from(this.cache.keys()),
      loadingPromises: this.loadingPromises.size
    };
  }
}

// Create a singleton instance
const masterDataService = new MasterDataService();

export default masterDataService; 